/**
 * This file defines types for permissions and adjacent concepts that are used to
 * evaluate authentication/authorization on websocket connections between
 * Collaboration and Client.
 */

import type WebSocket from "ws";

import type { CourseRole, OrgRole, SectionRole, UserRole } from "./roles";

/**
 * These roles only apply to Pubsub and Collaboration.
 *
 * In pubsub, these roles are used to determine whether a model update should be broadcast to a
 * user depending on whether they can see information generally available to either students or staff.
 * There might be cases where some staff can see content that other staff can't, in the future, in which
 * case this will need to be slightly reworked. Right now, this edge case doesn't exist.
 *
 * In collaboration, someone with a "staff" role can write to a Page's template and see all other user's
 * responses on a Page (but not modify them). Someone with a "student" role can have similar read access,
 * but it depends on a variety of factors, such as ownership, group membership, whether the "Class Responses" view
 * has been shown, etc. Student can never write to a Page's template, but by default, write to their own response
 * for Pages that they can also specifically read (e.g. released Pages).
 */
export type WebsocketRoleType = "student" | "staff";

/**
 * Used to set granular permissions for specific access IDs on a page.
 */
export type AccessIDPermissionType = "read" | "write";

// TODO: move server-types to shared/types and remove this duplicate enum
export enum InteractionMode {
  INDIVIDUAL = "individual",
  SHARED_CLASS = "all",

  // Instructors assign students to groups or students self-assign their groups.
  ASSIGN_STUDENTS = "assign_students",
  SELF_ASSIGN = "self_assign",
  COPY_GROUPS = "copy_groups",

  STATIC = "static",
}

/**
 * Specifies permissions of a user for a specific page. By default, unless overwritten,
 * an empty PagePermission means a user can read/write to the access ID matching their own user ID.
 * Instructors/Admins can read all access IDs by default, write to the "global" access ID,
 * and author the page content.
 * "rollup" indicates that the user can read-only view all other student/group access IDs.
 */
export type PagePermissionType = {
  id: string;
  worksheet_id: string;
  released_at: string | null;
  last_rolled_up_at: string | null;
  class_responses_released_at?: string | null;
  page_group_id?: string | null;
  locked_at?: string | null;
  interaction_mode?: InteractionMode;
};

export type PageRemovalPermissionType = {
  visible: false;
};

/**
 * Identity sent on pubsub WebSocket connect (`createAuthUpdateFromSocket`).
 * Redux persists only these fields in Redux (`auth.websocket`).
 */
export type PubsubConnectAuthData = {
  uniqueId: string;
  user_id: string;
  org_id: string;
  section_id: string | null;
};

/** Redux `auth.websocket`: fields Redux keeps from pubsub auth messages. */
export type StoredPubsubWebsocketIdentity = {
  user_id: string | null;
  org_id: string | null;
  section_id: string | null;
  uniqueId: string | null;
};

/**
 * Collaboration server: permission state on each Node WebSocket (`ws.userData`).
 * Loaded from Django on connect and updated from Redis auth-update messages.
 *
 * Django updates Collaboration when the permissions of a user change.
 * Collaboration sometimes re-validates permissions when a user tries
 * to perform an action, to prevent cases where an update goes missing or is stale.
 *
 * For update messages, the entirety of the permissions that are relevant for
 * Collaboration and the Pubsub forwarding system are serialized in one object,
 * which represent the new permission state. Any permissions not included are
 * assumed to be removed.
 */
export interface WebsocketUserDataType {
  user_id: string;
  email: string;
  // section_id is null/"null" on home. Home gets org-scoped updates, plus section-scoped
  // updates with also_target_home for org admins or home_target_user_ids.
  section_id: string | null;
  org_id: string;
  section_role: SectionRole | null;
  course_role: CourseRole | null;
  org_role: OrgRole | null;
  user_role: UserRole | null;
  uniqueId: string;
  pages: PagePermissionType[];
}

/**
 * Full auth-update payload from Django (Redis). Collaboration applies this to `ws.userData`;
 * it is not sent to browsers (only PubsubConnectAuthData on connect is).
 */
export interface WebsocketUserDataMessageType extends Omit<WebsocketUserDataType, "pages"> {
  scope: "individual" | "all";
  pages: PagePermissionType[];
  uniqueId: string;
}

export type ExtendedWsType = WebSocket & {
  userData: WebsocketUserDataType;
  uniqueId?: string;
  windowId?: string;
  isAlive?: boolean;
};

export interface WebsocketErrorType {
  code: number;
  message: string;
}

export type WebsocketAuthErrorType =
  | "DOC_CREATE_DISALLOWED"
  | "DOC_DELETE_DISALLOWED"
  | "DOC_GLOBAL_EDIT_DISALLOWED"
  | "MISSING_READ_PERMISSION"
  | "MISSING_WRITE_PERMISSION"
  | "MISSING_EDIT_PERMISSION"
  | "EDIT_MALFORMED"
  | "MALFORMED_RICHTEXT"
  | "DOC_READ_ONLY"
  | "OVERWRITE_DISALLOWED"
  | "MISSING_WRITE_PERMISSION_FOR_PATH"
  | "ACCESS_ID_WRAPPER_MALFORMED"
  | "FIELD_DATA_WRAPPER_MALFORMED"
  | "DOC_SIZE_LIMIT"
  | "OP_SIZE_LIMIT"
  | "ACCESS_PATH_SIZE_LIMIT"
  | "INSTRUCTOR_DATA_SIZE_LIMIT"
  | "UNKNOWN_ERROR";

export const AUTH_ERRORS: Record<WebsocketAuthErrorType, WebsocketAuthErrorType> = {
  DOC_CREATE_DISALLOWED: "DOC_CREATE_DISALLOWED",
  DOC_DELETE_DISALLOWED: "DOC_DELETE_DISALLOWED",
  DOC_GLOBAL_EDIT_DISALLOWED: "DOC_GLOBAL_EDIT_DISALLOWED",
  MISSING_READ_PERMISSION: "MISSING_READ_PERMISSION",
  MISSING_WRITE_PERMISSION: "MISSING_WRITE_PERMISSION",
  MISSING_WRITE_PERMISSION_FOR_PATH: "MISSING_WRITE_PERMISSION_FOR_PATH",
  ACCESS_ID_WRAPPER_MALFORMED: "ACCESS_ID_WRAPPER_MALFORMED",
  FIELD_DATA_WRAPPER_MALFORMED: "FIELD_DATA_WRAPPER_MALFORMED",
  MISSING_EDIT_PERMISSION: "MISSING_EDIT_PERMISSION",
  EDIT_MALFORMED: "EDIT_MALFORMED",
  MALFORMED_RICHTEXT: "MALFORMED_RICHTEXT",
  OVERWRITE_DISALLOWED: "OVERWRITE_DISALLOWED",
  DOC_READ_ONLY: "DOC_READ_ONLY",
  DOC_SIZE_LIMIT: "DOC_SIZE_LIMIT",
  OP_SIZE_LIMIT: "OP_SIZE_LIMIT",
  ACCESS_PATH_SIZE_LIMIT: "ACCESS_PATH_SIZE_LIMIT",
  INSTRUCTOR_DATA_SIZE_LIMIT: "INSTRUCTOR_DATA_SIZE_LIMIT",
  UNKNOWN_ERROR: "UNKNOWN_ERROR",
};

export const AUTH_ERROR_MESSAGES: Record<WebsocketAuthErrorType, string> = {
  DOC_CREATE_DISALLOWED: "Creating new documents is not allowed.",
  DOC_DELETE_DISALLOWED: "Creating new documents is not allowed.",
  DOC_GLOBAL_EDIT_DISALLOWED: "Can't modify the base document structure.",
  MISSING_READ_PERMISSION: "You don't have permission to view this document.",
  MISSING_WRITE_PERMISSION: "You don't have permission to write in this document.",
  MISSING_EDIT_PERMISSION: "You don't have permission to modify this document.",
  MISSING_WRITE_PERMISSION_FOR_PATH: "You don't have permission to write in this path.",
  ACCESS_ID_WRAPPER_MALFORMED: "This is not a valid access ID wrapper.",
  FIELD_DATA_WRAPPER_MALFORMED: "This is not a valid field data wrapper.",
  EDIT_MALFORMED: "The submitted operation does not fit the schema of the given path.",
  OVERWRITE_DISALLOWED: "Overwriting this data is not allowed.",
  MALFORMED_RICHTEXT: "The rich text operation includes invalid data.",
  DOC_READ_ONLY: "Can't edit a read-only document.",
  DOC_SIZE_LIMIT: "Document size exceeds limit.",
  OP_SIZE_LIMIT: "Operation size exceeds limit.",
  ACCESS_PATH_SIZE_LIMIT: "Your data size exceeds the allowed limit.",
  INSTRUCTOR_DATA_SIZE_LIMIT: "Instructor/template data size exceeds the allowed limit.",
  UNKNOWN_ERROR: "Failed to perform the requested operation.",
};
